<template>
    <form action="/probe-calibration/automated/create" class="space-y-6" method="GET">
        <!-- Banner to notify Iso Facility Environment is not validated -->
        <div    v-if="! isoFacilityEnvironmentValidated">
            <banner>
                <template   #title>
                    Facility Environment Status
                </template>
                <template   #content>
                    The facility environment status is not valid for today. Please update the facility environment status.
                    You will not be able to register calibrations until the facility environment status is
                    <undecorated-link   :href="isoFacilityValidationUrl">validated</undecorated-link>.
                </template>
            </banner>
        </div>
        
        <!-- Calibrator Selection -->
        <div>
            <label class="block text-sm font-medium text-gray-700" for="performed_by"> Calibrations Performed By </label>
            <div class="mt-1">
                <select2 :close-on-select="true"
                         :match-alpha-numeric="true"
                         :multiple="false"
                         :options='techInformation'
                         name="performed_by"
                         placeholder="Performer"
                ></select2>
            </div>
        </div>

        <!-- Approver Selection -->
        <div>
            <label class="block text-sm font-medium text-gray-700" for="approved_by"> Calibrations Approved By </label>
            <div class="mt-1">
                <select2 :close-on-select="true"
                         :match-alpha-numeric="true"
                         :multiple="false"
                         :options='techInformation'
                         name="approved_by"
                         placeholder="Approver"
                ></select2>
            </div>
        </div>

        <!-- Calibration Equipment Selection -->
        <div>
            <label  class="block text-sm font-medium text-gray-700" for="equipment_ids[]"> Calibration Equipment Used </label>
            <div class="mt-1">
                <select2    :close-on-select="true"
                            :match-alpha-numeric="true"
                            :multiple="false"
                            :options='calibrationEquipment'
                            name="equipment_ids[]"
                            placeholder="Calibration Equipment Used"
                ></select2>
            </div>
        </div>

        <!-- Tab Selection -->
        <div>
            <device-series-selection v-model:currentTab="currentTab"
            ></device-series-selection>

            <!-- Selected tab for form -->
            <div    v-show="false">
                <input  :value="currentTab"  name="tab">
            </div>
        </div>

        <!-- SoniShield Probe Info -->
        <div    v-if="currentTab === 'sonishield'">
            <auto-calibration-sonishield-setup
                    :area-zones="areaZones"
                    :probe-information="probeInformation"
                    :ult-testing-methods="ultTestingMethods"
                    :reference-probe-information="referenceProbeInformation">
            </auto-calibration-sonishield-setup>
        </div>

        <!-- S-Series Temperature Probe Info -->
        <div    v-if="currentTab === 's-series-temperature'">
            <auto-calibration-s-series-temperature
                    :reference-probe-information="referenceProbeInformation">
            </auto-calibration-s-series-temperature>
        </div>

        <!-- S-Series HT Probe Info -->
        <div    v-if="currentTab === 's-series-ht'">
            <automated-calibration-s-series-ht
                    :reference-probe-information="referenceProbeInformation">
            </automated-calibration-s-series-ht>
        </div>

        <!-- S-Series T/H/Co2 Probe Info -->
        <div    v-if="currentTab === 's-series-ht-co2'">
            <automated-calibration-s-series-ht-co2
                :reference-probe-information="referenceProbeInformation">
            </automated-calibration-s-series-ht-co2>
        </div>

        <!-- S-Series: Zone Leak Detector w/HT Probe Info -->
        <div    v-if="currentTab === 's-series-rope-ht'">
            <automated-calibration-s-series-ht
                :reference-probe-information="referenceProbeInformation">
            </automated-calibration-s-series-ht>
        </div>

        <!-- S-Series: Light Sensor -->
        <div    v-if="currentTab === 's-series-light'">
            <automated-calibration-s-series-light
                :reference-probe-information="referenceProbeInformation">
            </automated-calibration-s-series-light>
        </div>

        <!-- Submit Button -->
        <div>
            <button class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" type="submit">Start Calibrations</button>
        </div>
    </form>
</template>

<script>
import Select2 from "@/components/elements/select2.vue";
import Select2Group from "@/components/elements/select2-group.vue";

import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import Toggle from "@/components/elements/toggle.vue";
import UndecoratedLink from "@/components/EnterpriseDashboard/undecorated-link.vue";
import DeviceSeriesSelection from "@/components/AutoCalibration/DeviceSeriesSelection.vue";
import AutoCalibrationSonishieldSetup from "@/components/AutoCalibration/AutoCalibrationSonishieldSetup.vue";
import AutomatedCalibrationSSeriesHt from "@/components/AutoCalibration/AutomatedCalibrationSSeriesHt.vue";
import AutomatedCalibrationSSeriesHtCo2 from "@/components/AutoCalibration/AutomatedCalibrationSSeriesHtCo2.vue";
import AutoCalibrationSSeriesTemperature from "@/components/AutoCalibration/AutoCalibrationSSeriesTemperature.vue";
import AutomatedCalibrationSSeriesLight from "@/components/AutoCalibration/AutomatedCalibrationSSeriesLight.vue";
import Banner from "../Banners/Banner.vue";

export default {
    name: "AutomatedCalibrationIndex",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {
        Banner,
        AutomatedCalibrationSSeriesLight,
        AutoCalibrationSSeriesTemperature, AutomatedCalibrationSSeriesHt, AutomatedCalibrationSSeriesHtCo2,
        AutoCalibrationSonishieldSetup, DeviceSeriesSelection, UndecoratedLink, Toggle, Select2Group, Select2},
    props: {
        techInformation: {
            type: Object,
            required: true,
        },
        probeInformation: {
            type: Object,
            required: true,
        },
        ultTestingMethods: {
            type: Object,
            required: true,
        },
        referenceProbeInformation: {
            type: Object,
            required: true,
        },
        calibrationEquipment: {
            type: Object,
            required: true,
        },
        areaZones: {
            type: Object,
            required: true,
        },
        sSeriesTypes: {
            type: Object,
            required: false,
        },
    },
    data() {
        return {
            currentTab: 'sonishield',
            isNist: true,
            isoFacilityEnvironmentValidated: false,
        }
    },
    created() {

    },
    mounted() {
        this.validateIsoFacilityEnvironment();
    },
    methods: {
        validateIsoFacilityEnvironment() {
            axios.get(this.company.buildApiInternalRoute('facility-environment-status-valid'))
                .then(response => {
                    this.isoFacilityEnvironmentValidated = response.data.validated;
                })
                .catch(error => {
                    console.error(error);
                })
                .then(() => {
                    // If the facility environment is not validated, check again in 5 seconds
                    if (! this.isoFacilityEnvironmentValidated) {
                        setTimeout(() => {
                            this.validateIsoFacilityEnvironment()
                        }, 5000)
                    }
                });
        }
    },
    computed: {
        isoFacilityValidationUrl() {
            return '/nova/resources/facility-environment-sensor-status-resources'
        },
    },
    watch: {

    },
}
</script>